import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

export interface AppNotification {
  id: string;
  event_type: string;
  title: string;
  message: string;
  notification_type: "info" | "success" | "warning" | "error";
  source_module: string;
  timestamp: string;
  payload: Record<string, any>;
  read?: boolean;
}

interface NotificationContextValue {
  notifications: AppNotification[];
  toasts: AppNotification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismissToast: (id: string) => void;
  clearAll: () => void;
  connected: boolean;
}

const NotificationContext = createContext<NotificationContextValue>({
  notifications: [],
  toasts: [],
  unreadCount: 0,
  markAsRead: () => {},
  markAllAsRead: () => {},
  dismissToast: () => {},
  clearAll: () => {},
  connected: false,
});

export const useNotifications = () => useContext(NotificationContext);

const TOAST_DURATION = 6000;
const MAX_NOTIFICATIONS = 100;

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [toasts, setToasts] = useState<AppNotification[]>([]);
  const [connected, setConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Load history on mount
  useEffect(() => {
    fetch("/api/notifications/history?limit=50", { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (data.notifications) {
          const mapped: AppNotification[] = data.notifications.map(
            (n: any) => ({ ...n, read: true })
          );
          setNotifications(mapped.reverse());
        }
      })
      .catch(() => {});
  }, []);

  // SSE connection with auto-reconnect
  useEffect(() => {
    let cancelled = false;

    function connect() {
      if (cancelled) return;

      const es = new EventSource("/api/notifications/stream");
      eventSourceRef.current = es;

      es.onopen = () => {
        setConnected(true);
      };

      es.onmessage = (event) => {
        try {
          const notification: AppNotification = {
            ...JSON.parse(event.data),
            read: false,
          };

          setNotifications((prev) => {
            const next = [notification, ...prev];
            return next.slice(0, MAX_NOTIFICATIONS);
          });

          setToasts((prev) => [notification, ...prev]);

          // Auto-dismiss toast
          setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== notification.id));
          }, TOAST_DURATION);
        } catch {
          // ignore malformed messages
        }
      };

      es.onerror = () => {
        setConnected(false);
        es.close();
        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          if (!cancelled) connect();
        }, 3000);
      };
    }

    connect();

    return () => {
      cancelled = true;
      eventSourceRef.current?.close();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        toasts,
        unreadCount,
        markAsRead,
        markAllAsRead,
        dismissToast,
        clearAll,
        connected,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
